import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@legends/ui";
import { fetchFeaturedLegends, fetchLegendTaxonomy, logout, track, type LegendLite, type SportGroup } from "./api";
import { flattenLegendTaxonomy, matchesLegendSearch, teamsForSport } from "./legendDiscovery";
import { FeaturedLegendCard, LegendResultCard } from "./SelectorLegendCards";
import { AsyncState, LegendsBrand, monoLabelStyle, type LoadStatus } from "./sections";
import PublicLegalFooter from "./PublicLegalFooter";
import PublicSocialLinks from "./PublicSocialLinks";

export interface SelectorScreenProps {
  /** Invoked when a legend is chosen — the app routes to their Legend Cave. */
  onEnter: (legend: LegendLite) => void;
  /** Anonymous visitor: a Sign in link. */
  onSignIn?: () => void;
  /** Signed-in fan: a Sign out button. */
  onLogout?: () => void;
}

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

export default function SelectorScreen({ onEnter, onSignIn, onLogout }: SelectorScreenProps) {
  const [sports, setSports] = useState<SportGroup[]>([]);
  // The roster load is launch-critical, so distinguish loading / ready / error
  // rather than collapsing a failed fetch into an empty roster (ADR 0049).
  const [sportsStatus, setSportsStatus] = useState<LoadStatus>("loading");
  const [featured, setFeatured] = useState<LegendLite[]>([]);
  const [selectedSport, setSelectedSport] = useState<string | null>(null);
  const [selectedTeam, setSelectedTeam] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const loadTaxonomy = useCallback(() => {
    setSportsStatus("loading");
    fetchLegendTaxonomy()
      .then((s) => {
        setSports(s);
        setSportsStatus("ready");
      })
      .catch(() => {
        setSports([]);
        setSportsStatus("error");
      });
  }, []);

  useEffect(() => {
    track("cave_viewed", { caveId: "selector" });
    loadTaxonomy();
    // Featured is a best-effort enhancement above the funnel — a failure just
    // hides the row; the roster below stays the launch-critical read.
    // NOTE: we deliberately do NOT fire cave_impression from the featured rail (ADR 0206).
    // Featured is CURATION, not a browse list the fan scans — a non-click there isn't a
    // decline, and down-ranking a promoted cave for it is wrong. Both native clients reached
    // the same conclusion (schedule rows only). A featured legend's caveId is also the Cave
    // BACKEND, not the front on screen (ambiguous once a backend has multiple fronts). A
    // proper web impression on the search-results list (with dwell + per-session dedupe, like
    // the native clients) is a deferred follow-up.
    fetchFeaturedLegends().then(setFeatured).catch(() => setFeatured([]));
  }, [loadTaxonomy]);

  // Flatten taxonomy into all legends with metadata
  const allLegends = useMemo(() => flattenLegendTaxonomy(sports), [sports]);

  // Available sports options
  const availableSports = useMemo(() => {
    return sports.map((s) => s.sport);
  }, [sports]);

  // Available teams for current sport (Level is omitted from visible UI)
  const availableTeams = useMemo(() => teamsForSport(sports, selectedSport), [sports, selectedSport]);

  // Filtered legends
  const filteredLegends = useMemo(() => {
    return allLegends.filter((l) => {
      if (selectedSport !== null && l.sport !== selectedSport) return false;
      if (selectedTeam !== "all" && l.team !== selectedTeam) return false;
      if (!matchesLegendSearch(l, searchQuery)) return false;
      return true;
    });
  }, [allLegends, selectedSport, selectedTeam, searchQuery]);

  const isSearchingOrFiltered = useMemo(() => {
    return selectedSport !== null || selectedTeam !== "all" || searchQuery.trim().length >= 2;
  }, [selectedSport, selectedTeam, searchQuery]);

  const resetFilters = () => {
    setSelectedSport(null);
    setSelectedTeam("all");
    setSearchQuery("");
  };

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div style={{ minHeight: "calc(100vh / var(--ui-zoom, 1))", background: "var(--surface-base)", color: "var(--text)" }}>
      {/* SECTION 1: HEADER */}
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: "rgba(18, 20, 24, 0.88)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--hairline)",
          padding: "12px clamp(16px, 4vw, 40px)",
        }}
      >
        <div className="selector-header-inner" style={{ maxWidth: 1200, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <LegendsBrand />

          <nav className="selector-header-nav" style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <button
              onClick={() => scrollToSection("find-legend")}
              style={{
                background: "none",
                border: "none",
                padding: "6px 2px",
                cursor: "pointer",
                color: "var(--text-hi)",
                fontFamily: "var(--font-display)",
                fontSize: "14px",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                transition: "color .15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-hi)")}
            >
              Find Your Legend
            </button>

            <button
              onClick={() => scrollToSection("how-it-works")}
              style={{
                background: "none",
                border: "none",
                padding: "6px 2px",
                cursor: "pointer",
                color: "var(--text-hi)",
                fontFamily: "var(--font-display)",
                fontSize: "14px",
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                transition: "color .15s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--gold)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-hi)")}
            >
              How It Works
            </button>
          </nav>

          <div>
            {onLogout ? (
              <Button size="sm" variant="muted" onClick={() => logout().finally(() => onLogout())}>
                Sign out
              </Button>
            ) : onSignIn ? (
              <button
                onClick={onSignIn}
                aria-label="Sign in"
                style={{ background: "none", border: "none", cursor: "pointer", ...monoLabelStyle, color: "var(--gold)" }}
              >
                Sign in ▸
              </button>
            ) : null}
          </div>
        </div>
      </header>

      {/* SECTION 2: HERO */}
      <section
        style={{
          position: "relative",
          padding: "44px clamp(16px, 5vw, 48px) 36px",
          textAlign: "center",
          background: "radial-gradient(ellipse at 50% 0%, rgba(201, 162, 39, 0.12) 0%, rgba(18, 20, 24, 0) 70%)",
          borderBottom: "1px solid var(--hairline)",
          overflow: "hidden",
        }}
      >
        <div style={{ maxWidth: 900, margin: "0 auto" }}>

          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "clamp(36px, 6.5vw, 68px)",
              fontWeight: 800,
              lineHeight: 1.05,
              textTransform: "uppercase",
              color: "var(--text-hi)",
              letterSpacing: "0.02em",
            }}
          >
            SPORTS ARE BETTER WITH <span style={{ color: "var(--gold)", textShadow: "0 0 24px rgba(201, 162, 39, 0.25)" }}>SOMEONE WHO LIVED THEM.</span>
          </h1>

          <p
            style={{
              margin: "18px auto 26px",
              maxWidth: 640,
              fontSize: "clamp(16px, 2.2vw, 20px)",
              lineHeight: 1.5,
              color: "var(--text-prose)",
              fontWeight: 400,
            }}
          >
            Watch. Listen. Interact with the Legends who were there.
          </p>

          <div style={{ display: "flex", justifyContent: "center", gap: 16 }}>
            <Button
              size="md"
              variant="accent"
              onClick={() => scrollToSection("find-legend")}
              style={{
                padding: "14px 36px",
                fontSize: "15px",
                fontFamily: "var(--font-display)",
                letterSpacing: "0.05em",
              }}
            >
              FIND YOUR LEGEND ▸
            </Button>
          </div>
        </div>
      </section>

      <main style={{ maxWidth: 1200, margin: "0 auto", padding: "28px clamp(16px, 4vw, 40px)" }}>
        {/* SECTION 3: FEATURED LEGENDS */}
        {featured.length > 0 && (
          <section aria-label="Featured legends" style={{ marginBottom: 36 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
              <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "26px", textTransform: "uppercase", color: "var(--text-hi)" }}>
                Featured Legends
              </h2>
              <span aria-hidden style={{ flex: 1, height: 1, background: "linear-gradient(90deg, var(--hairline), transparent)", marginLeft: 24 }} />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
                gap: 20,
                maxWidth: featured.length <= 2 ? 800 : "none",
                margin: featured.length <= 2 ? "0 auto" : 0,
              }}
            >
              {featured.map((legend) => (
                <FeaturedLegendCard key={legend.id} legend={legend} onEnter={onEnter} />
              ))}
            </div>
          </section>
        )}

        {/* SECTION 4: FIND YOUR LEGEND (SIMPLIFIED DISCOVERY) */}
        <section id="find-legend" style={{ marginBottom: 40, scrollMarginTop: 76 }}>
          <div style={{ textAlign: "center", marginBottom: 20 }}>
            <h2
              className="selector-discovery-heading"
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: "clamp(28px, 4vw, 42px)",
                fontWeight: 700,
                textTransform: "uppercase",
                color: "var(--text-hi)",
              }}
            >
              <span className="selector-discovery-heading__phrase">FIND YOUR LEGEND.</span>
              <span className="selector-discovery-heading__phrase" style={{ color: "var(--gold)" }}>
                ENTER THEIR CAVE.
              </span>
            </h2>
            <p style={{ margin: "8px auto 0", maxWidth: 540, color: "var(--text-prose)", fontSize: "var(--fs-body)" }}>
              Search for your Legend or choose a sport to explore.
            </p>
          </div>

          {/* Launch-critical roster read: loading / error (with retry) / truly-empty
              are each distinct, so a failed fetch never reads as an empty roster
              (ADR 0049). Only the ready-with-legends path shows the search UI. */}
          {sportsStatus === "error" ? (
            <div style={{ textAlign: "center", padding: "36px 20px", maxWidth: 680, margin: "0 auto" }}>
              <AsyncState status="error" errorText="We couldn't load the Legends roster." onRetry={loadTaxonomy} />
            </div>
          ) : sportsStatus === "loading" ? (
            <div style={{ textAlign: "center", padding: "36px 20px", maxWidth: 680, margin: "0 auto" }}>
              <AsyncState status="loading" loadingText="Loading the Legends roster…" />
            </div>
          ) : allLegends.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px 20px", maxWidth: 680, margin: "0 auto" }}>
              <AsyncState status="empty" emptyText="No Legends are available yet." />
            </div>
          ) : (
          <>
          {/* PRIMARY SEARCH BAR & LIGHTWEIGHT BROWSE CONTROLS */}
          <div
            style={{
              maxWidth: 780,
              margin: "0 auto 20px",
              display: "flex",
              flexDirection: "column",
              gap: 16,
            }}
          >
            {/* SEARCH INPUT (PRIMARY CONTROL) */}
            <div style={{ position: "relative", width: "100%" }}>
              <input
                type="text"
                placeholder="Search by Legend, team, or sport..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "16px 20px 16px 48px",
                  borderRadius: "var(--radius-lg)",
                  background: "var(--surface-raised)",
                  border: `1px solid ${searchQuery ? "var(--gold)" : "var(--border)"}`,
                  color: "var(--text-hi)",
                  fontFamily: "var(--font-sans)",
                  fontSize: "16px",
                  boxShadow: searchQuery ? "var(--glow-accent)" : "0 4px 16px rgba(0,0,0,0.2)",
                  outline: "none",
                  transition: "all .2s ease-in-out",
                }}
              />
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: 18,
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: searchQuery ? "var(--gold)" : "var(--text-muted)",
                  fontSize: "18px",
                  fontFamily: "var(--font-mono)",
                  pointerEvents: "none",
                }}
              >
                ⌕
              </span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  style={{
                    position: "absolute",
                    right: 16,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "var(--text-muted)",
                    cursor: "pointer",
                    fontSize: "14px",
                  }}
                >
                  ✕
                </button>
              )}
            </div>

            {/* LIGHTWEIGHT SPORT BROWSE PILLS */}
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "center", gap: 8 }}>
              {availableSports.map((s) => (
                <button
                  key={s}
                  onClick={() => {
                    setSelectedSport(selectedSport === s ? null : s);
                    setSelectedTeam("all");
                  }}
                  style={{
                    fontFamily: "var(--font-sans)",
                    fontSize: "var(--fs-body-2)",
                    fontWeight: 500,
                    padding: "7px 15px",
                    borderRadius: 20,
                    cursor: "pointer",
                    border: `1px solid ${selectedSport === s ? "var(--gold)" : "var(--hairline)"}`,
                    background: selectedSport === s ? "var(--accent-tint)" : "var(--surface-quiet)",
                    color: selectedSport === s ? "var(--gold-2)" : "var(--text-muted)",
                    transition: "all .16s",
                  }}
                >
                  {cap(s)}
                </button>
              ))}
            </div>

            {/* SECONDARY PROGRESSIVE TEAM FILTER */}
            {selectedSport !== null && availableTeams.length > 0 && (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginTop: 2 }}>
                <span style={{ fontSize: "11px", color: "var(--text-dim)", fontFamily: "var(--font-sans)" }}>Team:</span>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  style={{
                    padding: "3px 10px",
                    borderRadius: 14,
                    background: "transparent",
                    border: "1px solid var(--hairline)",
                    color: "var(--text-muted)",
                    fontFamily: "var(--font-sans)",
                    fontSize: "12px",
                    cursor: "pointer",
                  }}
                >
                  <option value="all" style={{ background: "var(--surface-raised)", color: "var(--text)" }}>
                    All {cap(selectedSport)} Teams
                  </option>
                  {availableTeams.map((t) => (
                    <option key={t} value={t} style={{ background: "var(--surface-raised)", color: "var(--text)" }}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* LEGENDS DISCOVERY AREA */}
          {!isSearchingOrFiltered ? (
            <div style={{ textAlign: "center", paddingTop: 4 }}>
              <span
                style={{
                  display: "inline-block",
                  fontSize: "11px",
                  color: "var(--text-dim)",
                  ...monoLabelStyle,
                }}
              >
                Select a legend to enter the Cave
              </span>
            </div>
          ) : filteredLegends.length > 0 ? (
            <div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
                {filteredLegends.map((legend) => (
                  <LegendResultCard key={legend.id} legend={legend} onEnter={onEnter} />
                ))}
              </div>
            </div>
          ) : (
            <div
              style={{
                textAlign: "center",
                padding: "36px 20px",
                borderRadius: "var(--radius-md)",
                border: "1px dashed var(--hairline)",
                background: "var(--surface-quiet)",
                color: "var(--text-muted)",
                maxWidth: 680,
                margin: "0 auto",
              }}
            >
              <p style={{ margin: 0, fontSize: "var(--fs-body)" }}>No legends found matching current search.</p>
              <button
                onClick={resetFilters}
                style={{
                  marginTop: 12,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  ...monoLabelStyle,
                  color: "var(--gold)",
                }}
              >
                Clear search & filters
              </button>
            </div>
          )}
          </>
          )}
        </section>

        {/* SECTION 5: HOW LSC WORKS */}
        <section id="how-it-works" style={{ marginBottom: 40, scrollMarginTop: 76 }}>
          <div style={{ textAlign: "center", marginBottom: 22 }}>
            <h2
              style={{
                margin: 0,
                fontFamily: "var(--font-display)",
                fontSize: "clamp(28px, 4vw, 38px)",
                fontWeight: 700,
                textTransform: "uppercase",
                color: "var(--text-hi)",
              }}
            >
              HOW LEGEND SPORTS CAVE WORKS
            </h2>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 }}>
            {/* PILLAR 1: WATCH */}
            <div
              style={{
                padding: "28px 24px",
                borderRadius: "var(--radius-lg)",
                background: "var(--surface-quiet)",
                border: "1px solid var(--border-mute)",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "22px", color: "var(--text-hi)", textTransform: "uppercase" }}>
                WATCH
              </h3>
              <p style={{ margin: 0, color: "var(--text-prose)", fontSize: "var(--fs-body-2)", lineHeight: 1.5 }}>
                <strong style={{ color: "var(--text-hi)", display: "block", marginBottom: 6 }}>
                  Experience sports alongside the Legends who lived them.
                </strong>
                From live games to the moments and conversations that matter, see sports through a Legend's eyes.
              </p>
            </div>

            {/* PILLAR 2: LISTEN */}
            <div
              style={{
                padding: "28px 24px",
                borderRadius: "var(--radius-lg)",
                background: "var(--surface-quiet)",
                border: "1px solid var(--border-mute)",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "22px", color: "var(--text-hi)", textTransform: "uppercase" }}>
                LISTEN
              </h3>
              <p style={{ margin: 0, color: "var(--text-prose)", fontSize: "var(--fs-body-2)", lineHeight: 1.5 }}>
                <strong style={{ color: "var(--text-hi)", display: "block", marginBottom: 6 }}>
                  Hear the stories and perspective only a Legend can bring.
                </strong>
                The experiences, personalities and moments behind the game, told by someone who was there.
              </p>
            </div>

            {/* PILLAR 3: INTERACT */}
            <div
              style={{
                padding: "28px 24px",
                borderRadius: "var(--radius-lg)",
                background: "var(--surface-quiet)",
                border: "1px solid var(--border-mute)",
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              <h3 style={{ margin: 0, fontFamily: "var(--font-display)", fontSize: "22px", color: "var(--text-hi)", textTransform: "uppercase" }}>
                INTERACT
              </h3>
              <p style={{ margin: 0, color: "var(--text-prose)", fontSize: "var(--fs-body-2)", lineHeight: 1.5 }}>
                <strong style={{ color: "var(--text-hi)", display: "block", marginBottom: 6 }}>
                  Be part of the experience.
                </strong>
                Ask questions, join the conversation and interact directly with the Legend as it happens.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 6: CAVE EXPLANATION */}
        <section
          style={{
            marginBottom: 32,
            padding: "32px clamp(20px, 4vw, 48px)",
            borderRadius: "var(--radius-lg)",
            background: "linear-gradient(145deg, rgba(201, 162, 39, 0.08) 0%, rgba(26, 29, 33, 0.9) 100%)",
            border: "1px solid var(--gold-2)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontFamily: "var(--font-display)",
              fontSize: "clamp(26px, 3.5vw, 36px)",
              fontWeight: 700,
              textTransform: "uppercase",
              color: "var(--text-hi)",
            }}
          >
            EVERY LEGEND HAS A CAVE
          </h2>
          <p
            style={{
              margin: "16px 0 24px",
              maxWidth: 680,
              color: "var(--text-prose)",
              fontSize: "var(--fs-body)",
              lineHeight: 1.6,
            }}
          >
            <strong style={{ color: "var(--text-hi)", display: "block", marginBottom: 6 }}>
              A Cave is a Legend's home inside LSC.
            </strong>
            It's where fans discover and join the sports experiences that Legend wants to create. Every Cave is different because every Legend is different.
          </p>
          <Button size="md" variant="accent" onClick={() => scrollToSection("find-legend")}>
            FIND YOUR LEGEND ▸
          </Button>
        </section>
      </main>

      {/* SECTION 7: RESTRAINED FOOTER */}
      <footer
        style={{
          borderTop: "1px solid var(--hairline)",
          background: "rgba(14, 16, 19, 0.95)",
          padding: "32px clamp(16px, 4vw, 40px)",
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 20,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <LegendsBrand />
            <span style={{ color: "var(--text-dim)", fontSize: "var(--fs-2xs)" }}>|</span>
            <span style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>
              Sports are better with someone who lived them.
            </span>
          </div>

          <div className="selector-footer-actions">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
              <button
                onClick={() => scrollToSection("find-legend")}
                style={{ background: "none", border: "none", cursor: "pointer", ...monoLabelStyle, color: "var(--text-muted)", fontSize: "11px" }}
              >
                Find Your Legend
              </button>
              <button
                onClick={() => scrollToSection("how-it-works")}
                style={{ background: "none", border: "none", cursor: "pointer", ...monoLabelStyle, color: "var(--text-muted)", fontSize: "11px" }}
              >
                How It Works
              </button>
              {onSignIn && (
                <button
                  onClick={onSignIn}
                  style={{ background: "none", border: "none", cursor: "pointer", ...monoLabelStyle, color: "var(--gold)", fontSize: "11px" }}
                >
                  Sign In
                </button>
              )}
            </div>
            <PublicSocialLinks />
          </div>

          <div style={{ width: "100%", borderTop: "1px solid var(--hairline)", paddingTop: 16, marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontSize: "11px", color: "var(--text-dim)" }}>
              © 2026 Legend Sports Cave. All rights reserved.
            </span>
          </div>
          <PublicLegalFooter />
        </div>
      </footer>
    </div>
  );
}
